import { blockNumberRanges, blockTextLimits } from '@lib/customization/reportDesign';
import { newEntityId } from '@store/api/poiTypeEndpoints';
import { type ReportBlock, type ReportBlockType, type ReportFieldSource } from '@store/api/reportDesignEndpoints';
import {
  AlignLeftIcon, ArrowsAltVIcon, HeadingIcon, ImageIcon, ImagesIcon, MinusIcon, SignatureIcon, TagIcon,
  WindowMaximizeIcon,
} from 'react-line-awesome';

/**
 * What an author can put on a cover or a closing page, and what can be set on each: the
 * registry the palette, a block's toolbar on the canvas and the inspector are all drawn
 * from. Adding a block type to the editor is an entry here (and its type in
 * `reportDesignEndpoints`, its limits in `@lib/customization/reportDesign`). One component
 * names a block type besides: `canvas/BlockView.tsx`, which draws each as the report prints it.
 *
 * Every `label`, `description`, `help` and `placeholder` is a key of the localization CMS,
 * not text.
 *
 * A field's `fallback` is what the report draws while the key is absent, which is what the
 * control shows then. It is never written into the design: a default stored in a document
 * is today's default for ever.
 */

/** The icons are all the same component shape; naming it keeps `<Icon />` typed. */
export type CatalogIcon = typeof AlignLeftIcon;

export interface FieldOption {
  value: string | number;
  /** A CMS key. Left out where the value says it all: a number of columns. */
  label?: string;
}

interface FieldBase {
  /** The key of the block's `props` the field edits. */
  key: string;
  label: string;
  /** A sentence under the control. */
  help?: string;
  /** Shown only while this holds for the block's `props`. */
  when?: (props: Readonly<Record<string, unknown>>) => boolean;
}

export type BlockField = FieldBase & (
  /** Text by locale. `placeholder` names what is printed while it is empty. */
  | { kind: 'text'; max: number; multiline?: boolean; placeholder?: string }
  | { kind: 'number'; min: number; max: number; fallback: number }
  | { kind: 'toggle'; fallback: boolean }
  | { kind: 'select'; fallback: string | number; options: readonly FieldOption[] }
  /** An uploaded image of the design. */
  | { kind: 'image' }
  /** The values of a `fields` block. */
  | { kind: 'fieldItems' }
  /** The lines of a `signatures` block. */
  | { kind: 'signatureLines' }
);

export interface BlockDefinition {
  type: ReportBlockType;
  label: string;
  description: string;
  icon: CatalogIcon;
  /** The `props` a new block starts with. Left out where the block's defaults already draw something. */
  create?: () => Record<string, unknown>;
  fields: BlockField[];
}

const text = (
  type: ReportBlockType, key: string, label: string, rest: { multiline?: boolean; placeholder?: string; help?: string } = {},
): BlockField => ({ kind: 'text', key, label, max: blockTextLimits[type][key], ...rest });

const number = (type: ReportBlockType, key: string, label: string, fallback: number): BlockField =>
  ({ kind: 'number', key, label, fallback, ...blockNumberRanges[type][key] });

const columns = (most: number, fallback: number): BlockField => ({
  kind: 'select',
  key: 'columns',
  label: 'ReportDesign_Field_Columns',
  fallback,
  options: Array.from({ length: most }, (_, index) => ({ value: index + 1 })),
});

const left: FieldOption = { value: 'left', label: 'Common_Left' };
const center: FieldOption = { value: 'center', label: 'ReportDesign_Option_Center' };
const right: FieldOption = { value: 'right', label: 'Common_Right' };

const align = (fallback: string, options: FieldOption[] = [left, center, right]): BlockField =>
  ({ kind: 'select', key: 'align', label: 'ReportDesign_Field_Align', fallback, options });

export const blockCatalog: BlockDefinition[] = [
  {
    type: 'hero',
    label: 'ReportDesign_Block_Hero',
    description: 'ReportDesign_Block_Hero_Desc',
    icon: WindowMaximizeIcon,
    fields: [
      text('hero', 'overline', 'ReportDesign_Field_Overline', { placeholder: 'Inspection_Report' }),
      text('hero', 'title', 'Common_Title', { placeholder: 'Inspection_Name', help: 'ReportDesign_Placeholders' }),
      text('hero', 'subtitle', 'ReportDesign_Field_Subtitle', { placeholder: 'Project_Name' }),
      { kind: 'toggle', key: 'showRule', label: 'ReportDesign_Field_ShowRule', fallback: true },
      align('left', [left, center]),
    ],
  },
  {
    type: 'heading',
    label: 'ReportDesign_Block_Heading',
    description: 'ReportDesign_Block_Heading_Desc',
    icon: HeadingIcon,
    fields: [
      text('heading', 'text', 'ReportDesign_Field_Text', { help: 'ReportDesign_Placeholders' }),
      {
        kind: 'select',
        key: 'level',
        label: 'ReportDesign_Field_Style',
        fallback: 1,
        options: [
          { value: 1, label: 'ReportDesign_Option_HeadingTitle' },
          { value: 2, label: 'ReportDesign_Option_HeadingLabel' },
        ],
      },
    ],
  },
  {
    type: 'text',
    label: 'ReportDesign_Block_Text',
    description: 'ReportDesign_Block_Text_Desc',
    icon: AlignLeftIcon,
    fields: [
      text('text', 'content', 'ReportDesign_Field_Text', { multiline: true, help: 'ReportDesign_Placeholders' }),
      {
        kind: 'select',
        key: 'style',
        label: 'ReportDesign_Field_Style',
        fallback: 'normal',
        options: [
          { value: 'normal', label: 'ReportDesign_Option_Normal' },
          { value: 'muted', label: 'ReportDesign_Option_Muted' },
          { value: 'italic', label: 'ReportDesign_Option_Italic' },
        ],
      },
      number('text', 'size', 'ReportDesign_Field_Size', 9),
      align('left'),
    ],
  },
  {
    type: 'fields',
    label: 'ReportDesign_Block_Fields',
    description: 'ReportDesign_Block_Fields_Desc',
    icon: TagIcon,
    create: () => ({ items: [{ source: 'projectName' }, { source: 'inspectionDate' }] }),
    fields: [
      { kind: 'fieldItems', key: 'items', label: 'ReportDesign_Field_Items' },
      columns(4, 2),
    ],
  },
  {
    type: 'image',
    label: 'ReportDesign_Block_Image',
    description: 'ReportDesign_Block_Image_Desc',
    icon: ImageIcon,
    fields: [
      {
        kind: 'select',
        key: 'source',
        label: 'ReportDesign_Field_ImageSource',
        fallback: 'upload',
        options: [
          { value: 'upload', label: 'ReportDesign_Option_Upload' },
          { value: 'projectThumbnail', label: 'ReportDesign_Option_ProjectThumbnail' },
          { value: 'logo', label: 'ReportDesign_Logo' },
        ],
      },
      {
        kind: 'image',
        key: 'image',
        label: 'ReportDesign_Block_Image',
        when: (props) => (props.source ?? 'upload') === 'upload',
      },
      number('image', 'widthPercent', 'ReportDesign_Field_Width', 100),
      align('center'),
      { kind: 'toggle', key: 'frame', label: 'ReportDesign_Field_Frame', fallback: false },
    ],
  },
  {
    type: 'photoGrid',
    label: 'ReportDesign_Block_PhotoGrid',
    description: 'ReportDesign_Block_PhotoGrid_Desc',
    icon: ImagesIcon,
    fields: [columns(3, 2)],
  },
  {
    type: 'signatures',
    label: 'ReportDesign_Block_Signatures',
    description: 'ReportDesign_Block_Signatures_Desc',
    icon: SignatureIcon,
    fields: [
      { kind: 'signatureLines', key: 'lines', label: 'ReportDesign_Field_Lines' },
      { kind: 'toggle', key: 'showDate', label: 'ReportDesign_Field_ShowDate', fallback: true },
    ],
  },
  {
    type: 'divider',
    label: 'ReportDesign_Block_Divider',
    description: 'ReportDesign_Block_Divider_Desc',
    icon: MinusIcon,
    fields: [],
  },
  {
    type: 'spacer',
    label: 'ReportDesign_Block_Spacer',
    description: 'ReportDesign_Block_Spacer_Desc',
    icon: ArrowsAltVIcon,
    fields: [number('spacer', 'height', 'ReportDesign_Field_Height', 16)],
  },
];

/** A block's definition. None for a type this version does not know, which is shown and left alone. */
export const definitionOf = (type: string): BlockDefinition | undefined =>
  blockCatalog.find((definition) => definition.type === type);

/**
 * A block as the palette puts it on a page, under an id minted here. The server never mints
 * one: the id is what the editor selects and sorts by, before the first save as after it.
 */
export const createBlock = (type: ReportBlockType): ReportBlock => {
  const props = definitionOf(type)?.create?.();

  return { id: newEntityId(), type, ...(props ? { props } : {}) } as ReportBlock;
};

/**
 * What a `fields` block can print, with the key of each source's name. The names are the
 * ones the report prints over the value, so the picker and the page agree.
 */
export const fieldSourceLabels = {
  projectName: 'Project_Name',
  inspectionName: 'Inspection_Name',
  inspectionDate: 'Inspection_Date',
  modifiedDate: 'Common_DateModified',
  generatedDate: 'Report_Generated_Date',
  completion: 'Common_Completion',
  checkpointCount: 'Inspection_Checkpoints',
  operator: 'Report_Operator',
  metadata: 'ReportDesign_Source_Metadata',
} as const satisfies Record<ReportFieldSource, string>;
